import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

export default function ChipsBand() {
  const { t } = useLang();
  // Triple the list so users can drag left or right and always see content
  const items = [...t.chips, ...t.chips, ...t.chips];
  const scrollerRef = useRef(null);
  const rafRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragState = useRef({ startX: 0, startScroll: 0, moved: false });
  const [paused, setPaused] = useState(false);

  // Auto-scroll ticker
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Start in the middle so users can scroll both ways
    el.scrollLeft = el.scrollWidth / 3;

    let last = performance.now();
    const SPEED = 40; // pixels per second (auto-scroll)

    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!dragging && !paused && el) {
        el.scrollLeft += SPEED * dt;
        // Loop: if we've drifted past the third copy, snap back to middle
        const third = el.scrollWidth / 3;
        if (el.scrollLeft >= third * 2) el.scrollLeft -= third;
        else if (el.scrollLeft <= third * 0.1) el.scrollLeft += third;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [dragging, paused]);

  // Also loop when user manually scrolls to an edge
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const third = el.scrollWidth / 3;
    if (el.scrollLeft >= third * 2) el.scrollLeft -= third;
    else if (el.scrollLeft <= third * 0.1) el.scrollLeft += third;
  };

  const onPointerDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    setDragging(true);
    dragState.current = {
      startX: e.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    el.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const el = scrollerRef.current;
    if (!el) return;
    const dx = e.clientX - dragState.current.startX;
    if (Math.abs(dx) > 3) dragState.current.moved = true;
    el.scrollLeft = dragState.current.startScroll - dx;
  };
  const onPointerUp = (e) => {
    setDragging(false);
    try {
      scrollerRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {}
  };

  return (
    <div
      className="relative -mt-8 md:-mt-10 z-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="rounded-full bg-[var(--aa-cream-warm)] border border-[var(--aa-line)] shadow-[0_20px_50px_-30px_rgba(107,15,16,0.35)] px-3 md:px-4 py-3 overflow-hidden">
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className={`flex gap-3 whitespace-nowrap overflow-x-auto no-scrollbar select-none touch-pan-x ${
              dragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {items.map((c, i) => {
              const Ico = Icons[c.icon] || Icons.Sparkles;
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white border border-[var(--aa-line)] text-[13px] text-[var(--aa-wine)] font-medium flex-shrink-0 pointer-events-none"
                >
                  <Ico className="w-4 h-4" />
                  {c.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
